import React, { useEffect, useRef, useState, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const POLL_INTERVAL_MS = 5000;

/**
 * Status values returned by the backend
 * PENDING_UPLOAD → QUEUED → PROCESSING → COMPLETE | FAILED
 */
const STEPS = [
  { key: "PENDING_UPLOAD", label: "Uploaded",    icon: "✓" },
  { key: "QUEUED",         label: "Queued",       icon: "⟳" },
  { key: "PROCESSING",     label: "Analysing",    icon: "⬡" },
  { key: "COMPLETE",       label: "Complete",     icon: "✓" },
];

const STATUS_ORDER = ["PENDING_UPLOAD", "QUEUED", "PROCESSING", "COMPLETE"];

function currentStepIndex(status) {
  if (status === "FAILED") return -1;
  const idx = STATUS_ORDER.indexOf(status);
  return idx === -1 ? 0 : idx;
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <polyline points="20,6 9,17 4,12" />
    </svg>
  );
}

function SpinnerCircle() {
  return (
    <div
      style={{
        width: 14, height: 14,
        border: "2px solid rgba(59,130,246,0.2)",
        borderTop: "2px solid var(--accent-blue)",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }}
    />
  );
}

export default function StatusTracker({ jobId, onComplete }) {
  const { token } = useContext(AuthContext);
  const { addNotification } = useNotifications();
  const [status,      setStatus]      = useState("PENDING_UPLOAD");
  const [message,     setMessage]     = useState("Scan uploaded — waiting for worker to pick up job…");
  const [elapsed,     setElapsed]     = useState(0);
  const [failed,      setFailed]      = useState(false);
  const [failReason,  setFailReason]  = useState("");

  const startTime = useRef(Date.now());
  const pollRef   = useRef(null);
  const timerRef  = useRef(null);

  /* ── Elapsed timer ───────────────────────────────────────────────────────── */
  useEffect(() => {
    timerRef.current = setInterval(
      () => setElapsed(Math.floor((Date.now() - startTime.current) / 1000)),
      1000
    );
    return () => clearInterval(timerRef.current);
  }, []);

  /* ── Polling ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const poll = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res  = await axios.get(`${API_BASE}/api/job/${jobId}`, { headers });
        const data = res.data;

        setStatus(data.status);

        const statusMessages = {
          PENDING_UPLOAD: "Scan received — processing queue…",
          QUEUED:         "Job queued — waiting for GPU worker…",
          PROCESSING:     "AI analysis in progress — lung segmentation + U-Net inference…",
          COMPLETE:       "Analysis complete!",
          FAILED:         data.error || "An error occurred during analysis.",
        };
        setMessage(statusMessages[data.status] || data.status);

        if (data.status === "COMPLETE") {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          addNotification({
            type: "analysis",
            title: "Analysis Complete",
            message: `Lung CT scan analysis finished — ${data.nodule_count || 0} nodule(s) detected.`,
          });
          onComplete(data);
          return;
        }

        if (data.status === "FAILED") {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          setFailed(true);
          setFailReason(data.error || "Analysis failed. Please try again with a different scan.");
          addNotification({
            type: "system",
            title: "Analysis Failed",
            message: data.error || "CT scan analysis encountered an error.",
          });
        }
      } catch (err) {
        // Network error — keep polling; backend may be starting up
        setMessage("Connecting to analysis service…");
      }
    };

    poll(); // immediate first call
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [jobId, onComplete]);

  /* ── Helpers ─────────────────────────────────────────────────────────────── */
  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  const stepIdx = currentStepIndex(status);

  /* ── Render ──────────────────────────────────────────────────────────────── */
  return (
    <div style={{ maxWidth: 560, margin: "0 auto" }}>
      {/* Page title */}
      <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
        <h1 style={{ fontSize: "1.6rem", fontWeight: 700, letterSpacing: "-0.5px", marginBottom: 8 }}>
          {failed ? "Analysis Failed" : "Analysing Scan"}
        </h1>
        <p style={{ color: "var(--text-secondary)" }}>
          {failed ? "" : `Job ID: ${jobId}`}
        </p>
      </div>

      <div className="card">
        {/* Step indicator */}
        {!failed && (
          <div style={{ display: "flex", justifyContent: "space-between", position: "relative", marginBottom: "2rem" }}>
            {/* connector line */}
            <div style={{
              position: "absolute",
              top: 18, left: "10%", right: "10%",
              height: 2,
              background: "var(--border)",
              zIndex: 0,
            }} />
            <div style={{
              position: "absolute",
              top: 18, left: "10%",
              height: 2,
              width: `${Math.min(stepIdx / (STEPS.length - 1), 1) * 80}%`,
              background: "var(--accent-blue)",
              transition: "width 0.5s ease",
              zIndex: 1,
            }} />

            {STEPS.map((step, i) => {
              const done    = i < stepIdx;
              const active  = i === stepIdx && !failed;
              const pending = i > stepIdx;
              return (
                <div key={step.key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", zIndex: 2 }}>
                  <div style={{
                    width: 36, height: 36,
                    borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: done || (active && status === "COMPLETE")
                      ? "var(--accent-blue)"
                      : active
                      ? "rgba(59,130,246,0.15)"
                      : "var(--bg-input)",
                    border: `2px solid ${done || active ? "var(--accent-blue)" : "var(--border)"}`,
                    color: done ? "#fff" : active ? "var(--accent-blue)" : "var(--text-muted)",
                    fontSize: "0.85rem",
                    transition: "all 0.3s",
                    marginBottom: 8,
                  }}>
                    {done
                      ? <CheckIcon />
                      : active && status !== "COMPLETE"
                      ? <SpinnerCircle />
                      : <span style={{ fontSize: 12 }}>{i + 1}</span>}
                  </div>
                  <span style={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: done || active ? "var(--text-primary)" : "var(--text-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    textAlign: "center",
                  }}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Status message */}
        <div style={{
          padding:      "16px",
          background:   failed ? "rgba(239,68,68,0.08)" : "rgba(59,130,246,0.07)",
          border:       `1px solid ${failed ? "rgba(239,68,68,0.25)" : "rgba(59,130,246,0.2)"}`,
          borderRadius: "var(--radius-sm)",
          marginBottom: "1.25rem",
          display:      "flex",
          alignItems:   "center",
          gap:          12,
        }}>
          {!failed && <span className="spinner" />}
          <div>
            <p style={{ fontSize: "0.875rem", color: failed ? "var(--accent-red)" : "var(--text-primary)" }}>
              {failed ? failReason : message}
            </p>
          </div>
        </div>

        {/* Job details */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          {[
            { label: "Job ID",   value: jobId.slice(0, 8) + "…" },
            { label: "Elapsed",  value: fmt(elapsed) },
            { label: "Status",   value: status },
            { label: "Worker",   value: "g4dn.xlarge (T4 GPU)" },
          ].map(({ label, value }) => (
            <div key={label} style={{
              padding: "10px 14px",
              background: "var(--bg-input)",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--border)",
            }}>
              <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 3 }}>
                {label}
              </div>
              <div style={{ fontSize: "0.875rem", fontWeight: 600, fontFamily: label === "Job ID" ? "monospace" : "inherit" }}>
                {value}
              </div>
            </div>
          ))}
        </div>

        {/* Retry / info if failed */}
        {failed && (
          <div style={{ marginTop: "1.25rem", textAlign: "center" }}>
            <p style={{ color: "var(--text-secondary)", fontSize: "0.83rem", marginBottom: 12 }}>
              Check that the file is a valid DICOM series (ZIP of .dcm files) or a .mhd CT scan.
            </p>
            <button className="btn btn-secondary" onClick={() => window.location.reload()}>
              Try Again
            </button>
          </div>
        )}

        {!failed && (
          <p style={{ marginTop: "1.25rem", fontSize: "0.78rem", color: "var(--text-muted)", textAlign: "center" }}>
            Average analysis time: 2–5 minutes · This page updates automatically
          </p>
        )}
      </div>
    </div>
  );
}
